import getConfig from 'next/config';
import type { AxiosResponse } from 'axios';
import type { IncomingHttpHeaders } from 'http';
import { encode, type ParsedUrlQuery } from 'querystring';

import {
  donedealGateway,
  donedealServicesGateway,
} from '../api/api-utils/commonApiUtils';

import type {
  AdDetails,
  GetPhoneNumber,
  TrackAdCallNumber,
  HomePageResponse,
  ISectionsResponse,
} from '../api/types/adviewApiTypes';
import { HomePageResponseSchema } from './schemas/ad-view.schema';
import { PAGE } from '../helpers/pages';
import { logger } from '@dist-marketplace-frontend/services';

async function getAd(
  id: number,
  query?: ParsedUrlQuery,
  headers?: IncomingHttpHeaders,
): Promise<AxiosResponse<AdDetails>> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  // To support apollo tracking we need to forward query parameters
  // to the backend. We strip off those fields that are not needed
  const { adId, title, section, ...tidyQuery } = query ?? {};
  const forwardParams = `?${encode(tidyQuery)}`;

  // We manually override accept header to avoid errors
  const response: AxiosResponse<AdDetails> = await donedealServicesGateway.get(
    `/ddapi/v1/listings/${id}${forwardParams.length > 1 ? forwardParams : ''}`,
    {
      headers: { ...headers, accept: 'application/json' },
      timeout: parseInt(API_LISTINGS_TIMEOUT),
    },
  );

  return response;
}

async function getLatestAdId(): Promise<AxiosResponse<AdDetails>> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const response: AxiosResponse<AdDetails> = await donedealServicesGateway.get(
    '/ddapi/v1/health/listings',
    { timeout: parseInt(API_LISTINGS_TIMEOUT) },
  );

  return response;
}

async function getPhoneNumber(
  id: number,
  captchaToken: string,
  campaignId?: string | null,
  accessToken?: string,
): Promise<AxiosResponse<GetPhoneNumber>> {
  const response: AxiosResponse<GetPhoneNumber> = await donedealGateway.get(
    `/ddapi/v2/listings/${id}/phonereveal${
      campaignId && campaignId !== null ? `?campaign=${campaignId}` : ''
    }`,
    {
      headers: {
        'Recaptcha-Token': captchaToken,
        platform: 'web',
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
    },
  );
  return response;
}

async function trackAdCallNumber(
  id: number,
  campaignId?: string | null,
): Promise<AxiosResponse<TrackAdCallNumber>> {
  const response: AxiosResponse<TrackAdCallNumber> = await donedealGateway.get(
    `/ddapi/legacy/cadview/api/v3/log/${id}/call${
      campaignId && campaignId !== null ? `?campaign=${campaignId}` : ''
    }`,
  );
  return response;
}

async function getHomePageData(
  focus: 'motor' | 'marketplace' | 'farming',
  headers?: IncomingHttpHeaders,
  query?: ParsedUrlQuery,
  numberOfAds = 8,
): Promise<AxiosResponse<HomePageResponse>> {
  const forwardParams = `?${encode(query)}`;
  // We manually override accept header to avoid errors
  const config = { headers: { ...headers, accept: 'application/json' } };

  const response: AxiosResponse = await donedealServicesGateway.get(
    `/ddapi/v1/homepage/${focus}/${numberOfAds}${
      forwardParams.length > 1 ? forwardParams : ''
    }`,
    config,
  );

  const parsedResponse = HomePageResponseSchema.safeParse(response.data);

  if (!parsedResponse.success) {
    logger.error({
      error: parsedResponse.error,
      message: 'Failed to parse home page data',
      route: PAGE.HOME,
    });

    throw parsedResponse.error;
  }

  return {
    ...response,
    data: parsedResponse.data,
  };
}

async function getSections(
  headers: IncomingHttpHeaders,
): Promise<AxiosResponse<ISectionsResponse>> {
  const response: AxiosResponse<ISectionsResponse> =
    await donedealServicesGateway.get(
      `/ddapi/legacy/cadview/api/v5/homepage/sections`,
      { headers },
    );
  return response;
}

export const adViewApi = {
  getAd,
  getPhoneNumber,
  trackAdCallNumber,
  getHomePageData,
  getLatestAdId,
  getSections,
};
